import { useEffect, useRef, useState } from 'react'

// Carga Leaflet desde CDN una sola vez
let leafletLoading = null
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L)
  if (leafletLoading) return leafletLoading
  leafletLoading = new Promise((resolve, reject) => {
    // CSS
    const css = document.createElement('link')
    css.rel = 'stylesheet'
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(css)
    // JS
    const s = document.createElement('script')
    s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    s.onload = () => resolve(window.L)
    s.onerror = reject
    document.head.appendChild(s)
  })
  return leafletLoading
}

/**
 * Mapita con un pin draggable. Sirve para que el admin verifique/corrija
 * la ubicación geocodificada de un cliente.
 *
 * Props:
 * - lat, lng       → coords iniciales (centra el mapa y pone el pin)
 * - direccion      → texto a mostrar arriba (referencia visual)
 * - onChange(lat, lng) → callback cuando se arrastra el pin
 */
export default function MapPin({ lat, lng, direccion, onChange, height = 280 }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const [ready, setReady] = useState(false)

  // Carga inicial del mapa
  useEffect(() => {
    if (!lat || !lng) return
    let mounted = true
    loadLeaflet().then(L => {
      if (!mounted || !containerRef.current) return

      // Si ya hay mapa, lo destruimos (cambio de coords iniciales)
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        markerRef.current = null
      }

      const map = L.map(containerRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,  // mejor UX adentro de un form: no scroll-zoom accidental
        attributionControl: true,
      }).setView([lat, lng], 17)

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap',
      }).addTo(map)

      const icon = L.divIcon({
        className: 'mappin-marker',
        html: `<div style="
          background:#1A3461;
          color:#fff;
          width:36px;height:36px;
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 4px 10px rgba(0,0,0,.35);
          border:3px solid #fff;
          cursor:grab;
        "><span style="transform:rotate(45deg);font-size:18px;">📍</span></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
      })

      const marker = L.marker([lat, lng], { draggable: true, icon }).addTo(map)
      marker.on('dragend', () => {
        const p = marker.getLatLng()
        onChange?.(p.lat, p.lng)
      })
      // También permitir click en el mapa para mover el pin
      map.on('click', (e) => {
        marker.setLatLng(e.latlng)
        onChange?.(e.latlng.lat, e.latlng.lng)
      })

      mapRef.current = map
      markerRef.current = marker
      setReady(true)
      // Asegurar tamaño correcto (a veces el contenedor recién aparece)
      setTimeout(() => map.invalidateSize(), 100)
    })
    return () => { mounted = false }
  }, [lat, lng])  // si cambian las coords iniciales, se recrea

  if (!lat || !lng) return null

  return (
    <div className="mappin-wrap" style={{ marginTop: 8 }}>
      <div className="mappin-info">
        🗺️ <strong>Verificá el pin</strong> en el mapa. Si está mal, <strong>arrastralo</strong> al lugar correcto o <strong>tocá el mapa</strong> donde corresponda.
      </div>
      <div ref={containerRef} className="mappin-map" style={{ height }} />
      <style>{`
        .mappin-wrap { border: 1px solid var(--line); border-radius: 10px; overflow: hidden; background: #fff }
        .mappin-info {
          background: #FEF3C7; color: #92400E;
          padding: 8px 12px; font-size: 12px; line-height: 1.4;
          border-bottom: 1px solid #F59E0B;
        }
        .mappin-map { width: 100%; background: #E5E7EB }
        .mappin-map .leaflet-container { font-family: inherit }
      `}</style>
    </div>
  )
}
